import {
  fetchBillingStatus,
  type BillingStatus,
} from "@/features/subscription/api/billing-api";
import { hasRevenueCatPlus } from "@/lib/billing/revenuecat";
import { isNative } from "@/lib/capacitor/platform";

export type EntitlementCode =
  | "premium_experiments"
  | "coach_unlimited"
  | "programs";

let cache: { at: number; status: BillingStatus | null } = {
  at: 0,
  status: null,
};

const CACHE_MS = 30_000;

export async function getBillingStatusCached(
  force = false
): Promise<BillingStatus | null> {
  const now = Date.now();
  if (!force && cache.status && now - cache.at < CACHE_MS) {
    return cache.status;
  }
  const status = await fetchBillingStatus();
  cache = { at: now, status };
  return status;
}

/**
 * Plus access: backend subscription/entitlements, with native RevenueCat
 * customer info as a fast local fallback right after IAP.
 */
export async function hasEntitlement(
  code: EntitlementCode
): Promise<boolean> {
  const status = await getBillingStatusCached();
  if (status?.isPremium) return true;
  if (
    status?.entitlements.some(
      (e) => e.entitlement_code === code && e.active
    )
  ) {
    return true;
  }

  // Native: RevenueCat may already show Plus before webhook lands
  if (isNative) {
    try {
      if (await hasRevenueCatPlus()) return true;
    } catch {
      // ignore
    }
  }

  return false;
}

export function clearEntitlementCache() {
  cache = { at: 0, status: null };
}
